import React, { useState, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { createJournalEntryV2 } from '../../../api/journal';
import { useFirebase } from '../../../context/FirebaseContext';
import './SaveToJournalModal.css';

const SaveToJournalModal = ({ open, onClose, trade, validationResult, account }) => {
    const { auth } = useFirebase();
    const [outcome, setOutcome] = useState('WIN'); // 'WIN' | 'LOSS'
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    // Calculate risk and reward amounts
    const tradeMetrics = useMemo(() => {
        if (!trade) return null;

        const accountEquity = account?.equity || 100000;
        let riskAmount = 0;
        let riskPercent = 0;

        if (trade.riskDollar && parseFloat(trade.riskDollar) > 0) {
            riskAmount = parseFloat(trade.riskDollar);
            riskPercent = (riskAmount / accountEquity) * 100;
        } else if (trade.riskPercent && parseFloat(trade.riskPercent) > 0) {
            riskPercent = parseFloat(trade.riskPercent);
            riskAmount = (riskPercent / 100) * accountEquity;
        } else {
            riskPercent = 1;
            riskAmount = accountEquity * 0.01;
        }

        const desiredRr = trade.desiredRr || 1.5;
        const rewardAmount = riskAmount * desiredRr;

        return {
            riskAmount,
            riskPercent,
            rewardAmount,
            desiredRr,
            stopPrice: parseFloat(trade.stopPrice) || 0,
            targetPrice: parseFloat(trade.targetPrice) || 0,
            entryPrice: parseFloat(trade.entryPrice) || 0
        };
    }, [trade, account?.equity]);

    // Calculate default amount when outcome or trade changes
    React.useEffect(() => {
        if (!trade || !tradeMetrics) return;

        const calculatedAmount = outcome === 'WIN'
            ? tradeMetrics.rewardAmount
            : tradeMetrics.riskAmount;

        const signedAmount = outcome === 'WIN' ? calculatedAmount : -calculatedAmount;
        setAmount(signedAmount.toFixed(2));

    }, [outcome, trade, tradeMetrics]);

    // Get validation status
    const validationStatus = useMemo(() => {
        if (!validationResult) return null;
        const status = validationResult.status || validationResult.overallStatus;
        if (status === 'go' || status === 'GO') return 'GO';
        if (status === 'no_go' || status === 'NO_GO') return 'NO_GO';
        return null;
    }, [validationResult]);

    // Count passed/failed gates
    const gateStats = useMemo(() => {
        if (!validationResult?.gates) return { passed: 0, failed: 0, total: 0 };
        const gates = validationResult.gates;
        const passed = gates.filter(g => g.status === 'PASSED').length;
        const failed = gates.filter(g => g.status === 'FAILED').length;
        return { passed, failed, total: gates.length };
    }, [validationResult]);

    if (!open || !trade) return null;

    const handleSave = async () => {
        setSaving(true);
        try {
            // Build structured gates summary for better display later
            const gatesSummary = validationResult?.gates?.map(gate => ({
                name: gate.ruleName || gate.rule,
                status: gate.status,
                message: gate.message,
                type: gate.type || 'static'
            })) || [];

            const entry = {
                title: `${trade.instrument} ${trade.direction} Trade`,
                content: notes,
                instrument: trade.instrument,
                trade_type: trade.direction,
                profit_loss: parseFloat(amount),
                mood: '', // Optional
                lessons: JSON.stringify({
                    validationStatus,
                    gateStats,
                    gates: gatesSummary,
                    tradeSetup: {
                        stopPrice: tradeMetrics?.stopPrice,
                        targetPrice: tradeMetrics?.targetPrice,
                        riskAmount: tradeMetrics?.riskAmount,
                        riskPercent: tradeMetrics?.riskPercent,
                        desiredRr: tradeMetrics?.desiredRr
                    }
                }),
                trade_date: Date.now(),
                entry_price: parseFloat(trade.entryPrice),
                exit_price: outcome === 'WIN' ? parseFloat(trade.targetPrice) : parseFloat(trade.stopPrice),
                entry_size: null,
                return_percentage: tradeMetrics ? (parseFloat(amount) / tradeMetrics.riskAmount) * 100 : null,
                method_analytics: 'concept_gate',
            };

            await createJournalEntryV2(auth, (result) => {
                if (result && !result.error) {
                    onClose(true); // Signal success
                } else {
                    console.error('Failed to save journal entry', result);
                }
                setSaving(false);
            }, entry);

        } catch (error) {
            console.error('Error saving journal entry:', error);
            setSaving(false);
        }
    };

    return (
        <div className="save-journal-modal-overlay" onClick={onClose}>
            <div className="save-journal-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">Save to Trading Journal</h2>
                    <button className="modal-close-btn" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="modal-content">
                    {/* Trade Summary Card */}
                    <div className="journal-trade-summary">
                        <div className="trade-header-row">
                            <div className="trade-instrument">
                                <span className="instrument-name">{trade.instrument}</span>
                                <span className={`direction-badge ${trade.direction?.toLowerCase()}`}>
                                    {trade.direction === 'LONG' ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                                    {trade.direction}
                                </span>
                            </div>
                            {validationStatus && (
                                <div className={`validation-badge ${validationStatus === 'GO' ? 'go' : 'no-go'}`}>
                                    {validationStatus === 'GO' ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                    {validationStatus === 'GO' ? 'PASSED' : 'MISMATCHED'}
                                </div>
                            )}
                        </div>

                        <div className="trade-prices-grid">
                            <div className="price-item entry">
                                <span className="price-label">Entry</span>
                                <span className="price-value">{trade.entryPrice}</span>
                            </div>
                            <div className="price-item stop">
                                <span className="price-label">Stop</span>
                                <span className="price-value">{trade.stopPrice}</span>
                            </div>
                            <div className="price-item target">
                                <span className="price-label">Target</span>
                                <span className="price-value">{trade.targetPrice}</span>
                            </div>
                            <div className="price-item rr">
                                <span className="price-label">R:R</span>
                                <span className="price-value">1:{tradeMetrics?.desiredRr?.toFixed(1) || '1.5'}</span>
                            </div>
                        </div>

                        {gateStats.total > 0 && (
                            <div className="gates-summary">
                                <span className="gates-label">Validation:</span>
                                <span className="gates-passed">{gateStats.passed} passed</span>
                                {gateStats.failed > 0 && <span className="gates-failed">{gateStats.failed} failed</span>}
                            </div>
                        )}
                    </div>

                    {/* Outcome Selection */}
                    <div className="journal-form-section">
                        <span className="journal-form-label">How did the trade end?</span>
                        <div className="outcome-toggle-group">
                            <button
                                className={`outcome-btn win ${outcome === 'WIN' ? 'active' : ''}`}
                                onClick={() => setOutcome('WIN')}
                            >
                                <TrendingUpIcon fontSize="small" />
                                Hit Target (WIN)
                            </button>
                            <button
                                className={`outcome-btn loss ${outcome === 'LOSS' ? 'active' : ''}`}
                                onClick={() => setOutcome('LOSS')}
                            >
                                <TrendingDownIcon fontSize="small" />
                                Hit Stop (LOSS)
                            </button>
                        </div>
                    </div>

                    {/* P/L Amount */}
                    <div className="journal-form-section">
                        <span className="journal-form-label">
                            Profit/Loss
                            <span className="label-hint">
                                (Risk: ${tradeMetrics?.riskAmount?.toFixed(2) || '0'})
                            </span>
                        </span>
                        <div className="amount-input-wrapper">
                            <span className="currency-symbol">$</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                className={`journal-amount-input ${parseFloat(amount) >= 0 ? 'positive' : 'negative'}`}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                step="0.01"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="journal-form-section">
                        <span className="journal-form-label">Notes (optional)</span>
                        <textarea
                            className="journal-notes-area"
                            placeholder="What did you observe? Any lessons learned?"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save to Journal'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaveToJournalModal;
